import { zodResolver } from '@hookform/resolvers/zod';
import { TrashIcon } from '@phosphor-icons/react';
import { PlusCircleIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Autocomplete, useApiMutation } from '@/modules/common';
import { useEventProductSets, useStationProducts } from '@/modules/events/api';
import {
  ProductSet,
  ProductSetConflict,
  UpdateProductSetPayload,
} from '@/modules/events/types';

import { ProductSetFormValues, productSetSchema } from '../schema';
import { ProductSetConflictsDialog } from './product-set-conflicts-dialog';

const emptyProduct = { productId: '', size: '', amount: 1 };

// The conflicts endpoint accepts at most 200 pairs per call.
const MAX_CONFLICT_ITEMS = 200;

interface AddEditProductSetDialogProps {
  isOpen: boolean;
  eventId: string;
  stationId: string;
  productSet: ProductSet | null;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function AddEditProductSetDialog({
  isOpen,
  eventId,
  stationId,
  productSet,
  onOpenChange,
  onSuccess,
}: AddEditProductSetDialogProps) {
  const isEdit = !!productSet;

  const { products, isFetching: isFetchingProducts } = useStationProducts(
    eventId,
    stationId,
    isOpen
  );

  // The list query is disabled here — only the mutations are needed.
  const { createProductSetMutation, checkConflictsMutation } =
    useEventProductSets(eventId, stationId, false);

  const [conflicts, setConflicts] = useState<ProductSetConflict[]>([]);
  const [pendingValues, setPendingValues] =
    useState<ProductSetFormValues | null>(null);

  const updateProductSetMutation = useApiMutation<
    UpdateProductSetPayload,
    ProductSet
  >(
    productSet
      ? `/events/${eventId}/stations/${stationId}/product-sets/${productSet.id}`
      : undefined,
    {
      method: 'PATCH',
    },
    {
      onError: (error) => {
        toast.error(error.message);
      },
    }
  );

  const defaultValues: ProductSetFormValues = useMemo(
    () =>
      productSet
        ? {
            name: productSet.name,
            products: productSet.products.map(
              ({ id, productId, size, amount }) => ({
                id,
                productId,
                size,
                amount,
              })
            ),
          }
        : { name: '', products: [emptyProduct] },
    [productSet]
  );

  const form = useForm<ProductSetFormValues>({
    resolver: zodResolver(productSetSchema),
    defaultValues,
  });

  const { control, watch, setValue, handleSubmit } = form;

  // keyName is renamed because a product set item carries its own `id`, which
  // useFieldArray would otherwise shadow with its generated React key.
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'products',
    keyName: 'fieldKey',
  });

  const selectedProducts = watch('products');

  const productOptions = useMemo(
    () =>
      products.map((product) => ({
        id: product.id,
        value: [product.name, product.nickname, product.styleNumber]
          .filter(Boolean)
          .join(' · '),
      })),
    [products]
  );

  // Sizes of the row's product, minus the ones another row already claimed for
  // the same product — the set cannot hold the same product/size pair twice.
  const getSizeOptions = (index: number) => {
    const productId = selectedProducts?.[index]?.productId;

    if (!productId) return [];

    const takenSizes = (selectedProducts ?? [])
      .filter(
        (item, itemIndex) => itemIndex !== index && item.productId === productId
      )
      .map((item) => item.size);

    return (products.find((product) => product.id === productId)?.sizes ?? [])
      .filter((size) => !takenSizes.includes(size.size))
      .map((size) => ({ id: size.size, value: size.size }));
  };

  const submitProductSet = async (values: ProductSetFormValues) => {
    try {
      if (isEdit) {
        // The whole desired contents go out: kept items carry their id so the
        // backend updates them, new ones have none, and anything left out here
        // is removed from the set.
        await updateProductSetMutation.mutateAsync({
          name: values.name,
          products: values.products.map(({ id, productId, size, amount }) => ({
            ...(id ? { id } : {}),
            productId,
            size,
            amount,
          })),
        });
      } else {
        await createProductSetMutation.mutateAsync({
          name: values.name,
          products: values.products.map(({ productId, size, amount }) => ({
            productId,
            size,
            amount,
          })),
        });
      }

      toast.success(
        `Product set “${values.name}” ${isEdit ? 'updated' : 'created'} successfully!`
      );

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error(error.message);
    }
  };

  // Overlapping with another set is advisory: it only asks for confirmation
  // first, so a check that fails falls straight through to saving.
  const onSubmit = async (values: ProductSetFormValues) => {
    try {
      const foundConflicts = await checkConflictsMutation.mutateAsync({
        ...(productSet ? { excludeProductSetId: productSet.id } : {}),
        items: values.products
          .slice(0, MAX_CONFLICT_ITEMS)
          .map(({ productId, size }) => ({ productId, size })),
      });

      if (foundConflicts?.length) {
        setConflicts(foundConflicts);
        setPendingValues(values);
        return;
      }
    } catch (error) {
      console.error(error.message);
    }

    await submitProductSet(values);
  };

  const closeConflictsDialog = () => {
    setConflicts([]);
    setPendingValues(null);
  };

  const confirmConflicts = async () => {
    if (!pendingValues) return;

    await submitProductSet(pendingValues);
    closeConflictsDialog();
  };

  const isPending =
    checkConflictsMutation.isPending ||
    createProductSetMutation.isPending ||
    updateProductSetMutation.isPending;

  return (
    <>
      <Dialog onOpenChange={onOpenChange} open={isOpen}>
        <DialogContent className="sm:max-w-[720px]">
          <DialogHeader>
            <DialogTitle>
              {isEdit ? 'Edit product set' : 'Create product set'}
            </DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleSubmit(onSubmit)(event);
              }}
            >
              <div className="flex flex-col gap-4">
                <FormField
                  control={control}
                  name="name"
                  render={({ field, formState: { errors } }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter a name"
                          maxLength={50}
                          error={!!errors?.name}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div>
                  <p className="text-sm font-medium mb-2">Products</p>
                  <ScrollArea className="max-h-[320px]">
                    <div className="flex flex-col gap-3 pr-3">
                      {fields.map((item, index) => (
                        <div
                          key={item.fieldKey}
                          className="flex items-start gap-2"
                        >
                          <div className="flex-1 min-w-0">
                            <Autocomplete
                              name={`products.${index}.productId`}
                              control={control}
                              searchable
                              placeholder="Select product"
                              options={productOptions}
                              isLoading={isFetchingProducts}
                              onChange={() =>
                                setValue(`products.${index}.size`, '', {
                                  shouldValidate: false,
                                })
                              }
                            />
                          </div>

                          <div className="w-[140px]">
                            <Autocomplete
                              name={`products.${index}.size`}
                              control={control}
                              searchable
                              placeholder="Size"
                              options={getSizeOptions(index)}
                              isDisabled={!selectedProducts?.[index]?.productId}
                            />
                          </div>

                          <FormField
                            control={control}
                            name={`products.${index}.amount`}
                            render={({ field, fieldState }) => (
                              <FormItem className="w-[110px]">
                                <FormControl>
                                  <Input
                                    type="number"
                                    min={1}
                                    placeholder="Amount"
                                    error={!!fieldState.error}
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="mt-1"
                            data-testid="remove-product-set-product-button"
                            disabled={fields.length === 1}
                            onClick={() => remove(index)}
                          >
                            <TrashIcon size={20} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>

                  <Button
                    type="button"
                    variant="outline"
                    className="mt-3"
                    data-testid="add-product-set-product-button"
                    startIcon={<PlusCircleIcon size={16} />}
                    onClick={() => append(emptyProduct)}
                  >
                    Add product
                  </Button>
                </div>
              </div>

              <DialogFooter className="mt-6 gap-4">
                <DialogClose asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-[130px]"
                    data-testid="cancel-button"
                    disabled={isPending}
                  >
                    Cancel
                  </Button>
                </DialogClose>
                <Button
                  type="submit"
                  className="w-[130px]"
                  data-testid="save-button"
                  isLoading={isPending}
                >
                  {isEdit ? 'Save' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <ProductSetConflictsDialog
        isOpen={!!pendingValues}
        isPending={isPending}
        conflicts={conflicts}
        isEdit={isEdit}
        onConfirm={confirmConflicts}
        onCancel={closeConflictsDialog}
      />
    </>
  );
}
